/**
 * useSkillVersionDetail.ts — on-demand fetch state for ONE skill config
 * version (story E0-BO-CFG-SKILL-REGISTRY-ADMIN).
 *
 * Fetches `GET /api/config/skills/{skill}/versions/{version}` whenever
 * `params` identifies a version; `params === null` means "nothing to fetch"
 * (the idle phase) — used by both the compare view (two instances) and the
 * edit form (one instance, for the base version). Race-safety mirrors
 * useLeadDetail: a monotonic request id + mounted guard.
 */

import { useEffect, useRef, useState } from 'react';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { SkillVersionDetail } from '../../api/types';

export type SkillVersionDetailPhase =
  'idle' | 'loading' | 'ready' | 'unauthorized' | 'notFound' | 'error';

export interface SkillVersionDetailParams {
  skillName: string;
  configVersion: string;
  productCode: string;
}

export interface SkillVersionDetailState {
  detail: SkillVersionDetail | null;
  phase: SkillVersionDetailPhase;
}

export const useSkillVersionDetail = (
  apiClient: ApiClient,
  params: SkillVersionDetailParams | null,
): SkillVersionDetailState => {
  const [detail, setDetail] = useState<SkillVersionDetail | null>(null);
  const [phase, setPhase] = useState<SkillVersionDetailPhase>('idle');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (params == null) {
      setDetail(null);
      setPhase('idle');
      return;
    }
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    apiClient
      .getSkillVersionDetail(params.skillName, params.configVersion, {
        product_code: params.productCode,
      })
      .then((payload) => {
        if (!isCurrent()) return;
        setDetail(payload);
        setPhase('ready');
      })
      .catch((error: unknown) => {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setPhase('unauthorized');
        } else if (error instanceof ApiError && error.status === 404) {
          setPhase('notFound');
        } else {
          setPhase('error');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, params?.skillName, params?.configVersion, params?.productCode]);

  return { detail, phase };
};
